// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module PageErrorBoundary
 * @package @geenius/analytics/solidjs
 * @description Internal page-level recovery boundary shared by SolidJS
 * analytics page compositions.
 */

import { Card, CardContent } from "@geenius/ui/solidjs";
import { type Component, ErrorBoundary, type JSX, Show } from "solid-js";
import {
  type AnalyticsErrorStateValue,
  ErrorState,
} from "../components/ErrorState";

interface PageErrorBoundaryProps {
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  children: JSX.Element;
}

const PageErrorCard: Component<{
  error: AnalyticsErrorStateValue;
  onRetry?: () => void;
}> = (props) => (
  <Card data-analytics-page-error>
    <CardContent class="pt-6">
      <ErrorState error={props.error} onRetry={props.onRetry} />
    </CardContent>
  </Card>
);

/** Wraps page content with explicit data-error and render-boundary recovery. */
export const PageErrorBoundary: Component<PageErrorBoundaryProps> = (props) => {
  const hasExplicitError = () =>
    props.error !== null && props.error !== undefined;

  return (
    <Show
      when={!hasExplicitError()}
      fallback={<PageErrorCard error={props.error} onRetry={props.onRetry} />}
    >
      <ErrorBoundary
        fallback={(error, reset) => (
          <PageErrorCard
            error={error instanceof Error ? error : String(error)}
            onRetry={() => {
              reset();
              props.onRetry?.();
            }}
          />
        )}
      >
        {props.children}
      </ErrorBoundary>
    </Show>
  );
};
